import type { OpsLogisticDefinition } from '../types';
import { TRANSLATED_LEGACY_SCHOOL_LOGISTICS_CATALOG } from './legacySchoolLogisticTranslation';

/**
 * Canonical logistics for partner-season flows.
 *
 * Legacy `school.*` IDs remain in logisticsCatalog.ts so historical Ops
 * snapshots continue to render. New partner-season templates should use only
 * these IDs and the Partner → Location → Squad storage kinds.
 */
const BASE_PARTNER_LOGISTICS_CATALOG: ReadonlyArray<OpsLogisticDefinition> = [
  {
    id: 'partner.name',
    label: 'Partner name',
    scope: 'partner',
    type: 'text',
    storage: { kind: 'partners', fieldPath: 'name' },
    systemRequired: true,
    gates: ['other'],
    completeness: { kind: 'nonEmpty' },
  },
  {
    id: 'partner.timezone',
    label: 'Partner timezone',
    scope: 'partner',
    type: 'text',
    storage: { kind: 'partners', fieldPath: 'timezone' },
    systemRequired: true,
    gates: ['scheduling'],
    completeness: { kind: 'nonEmpty' },
  },
  {
    id: 'partner.parentCommunicationPolicy.directOutreachAllowed',
    label: 'May contact parents directly',
    scope: 'partner',
    type: 'boolean',
    storage: { kind: 'partners', fieldPath: 'parentCommunicationPolicy.directOutreachAllowed' },
    systemRequired: false,
    gates: ['parent_comms'],
    completeness: { kind: 'exists' },
  },
  {
    id: 'partner.parentCommunicationPolicy.parentAccountsAllowed',
    label: 'Parent accounts allowed',
    scope: 'partner',
    type: 'boolean',
    storage: { kind: 'partners', fieldPath: 'parentCommunicationPolicy.parentAccountsAllowed' },
    systemRequired: false,
    gates: ['parent_comms'],
    completeness: { kind: 'exists' },
  },
  {
    id: 'partner.billing.paymentMode',
    label: 'Default payment mode',
    scope: 'partner',
    type: 'enum',
    storage: { kind: 'partners', fieldPath: 'billingDefaults.paymentMode' },
    systemRequired: false,
    gates: ['invoicing'],
    completeness: { kind: 'nonEmpty' },
    enumOptions: [
      { id: 'stripe', label: 'Parents pay through Stripe' },
      { id: 'school_flat_fee', label: 'Partner flat fee' },
      { id: 'school_enrollment', label: 'Partner pays by enrollment' },
    ],
  },
  {
    id: 'partner.billing.proratedAllowed',
    label: 'Prorating allowed by default',
    scope: 'partner',
    type: 'boolean',
    storage: { kind: 'partners', fieldPath: 'billingDefaults.proratedAllowed' },
    systemRequired: false,
    gates: ['invoicing'],
    completeness: { kind: 'exists' },
  },
  {
    id: 'partner.billingProfile.legalName',
    label: 'Billing legal name',
    scope: 'partner',
    type: 'text',
    storage: { kind: 'partners', fieldPath: 'billingDefaults.legalName' },
    systemRequired: false,
    gates: ['invoicing'],
    completeness: { kind: 'nonEmpty' },
  },
  {
    id: 'partner.billingProfile.paymentPreference',
    label: 'Billing payment preference',
    scope: 'partner',
    type: 'enum',
    storage: { kind: 'partners', fieldPath: 'billingDefaults.paymentPreference' },
    systemRequired: false,
    gates: ['invoicing'],
    completeness: { kind: 'nonEmpty' },
    enumOptions: [
      { id: 'check', label: 'Check' },
      { id: 'ach', label: 'ACH' },
      { id: 'card', label: 'Card' },
      { id: 'portal', label: 'Portal' },
      { id: 'other', label: 'Other' },
    ],
  },
  {
    id: 'partnerSeason.commitment.statement',
    label: 'Seasonal commitment',
    scope: 'partner_season',
    type: 'text',
    storage: { kind: 'partner_seasons', fieldPath: 'commitment.statement' },
    systemRequired: true,
    gates: ['scheduling'],
    completeness: { kind: 'nonEmpty' },
  },
  {
    id: 'partnerSeason.registrationStartDate',
    label: 'Registration start date',
    scope: 'partner_season',
    type: 'date',
    storage: { kind: 'partner_seasons', fieldPath: 'registrationStartDate' },
    systemRequired: false,
    gates: ['marketing', 'scheduling'],
    completeness: { kind: 'nonEmpty' },
  },
  {
    id: 'partnerSeason.registrationEndDate',
    label: 'Registration end date',
    scope: 'partner_season',
    type: 'date',
    storage: { kind: 'partner_seasons', fieldPath: 'registrationEndDate' },
    systemRequired: false,
    gates: ['marketing', 'scheduling'],
    completeness: { kind: 'nonEmpty' },
  },
  {
    id: 'partnerSeason.marketing.plannedDeliverables',
    label: 'Planned partner marketing',
    scope: 'partner_season',
    type: 'string_list',
    storage: { kind: 'partner_seasons', fieldPath: 'marketing.plannedDeliverables' },
    systemRequired: false,
    gates: ['marketing'],
    completeness: { kind: 'exists' },
  },
  {
    id: 'partnerSeason.marketing.sentDeliverables',
    label: 'Marketing sent to partner',
    scope: 'partner_season',
    type: 'string_list',
    storage: { kind: 'partner_seasons', fieldPath: 'marketing.sentDeliverables' },
    systemRequired: false,
    gates: ['marketing'],
    completeness: { kind: 'exists' },
  },
  {
    id: 'partner.registrationLink',
    label: 'External registration URL',
    scope: 'partner',
    type: 'url',
    storage: { kind: 'partners', fieldPath: 'registrationLink' },
    systemRequired: false,
    gates: ['marketing'],
    completeness: { kind: 'nonEmpty' },
    helpText: 'Parent-facing MyRec, RecDesk, or school portal URL used on the public school page for non-Stripe programs. Locations may override.',
  },
  {
    id: 'location.registrationLink',
    label: 'Location registration link',
    scope: 'location',
    type: 'url',
    storage: { kind: 'locations', fieldPath: 'registrationLink' },
    systemRequired: false,
    gates: ['marketing'],
    completeness: { kind: 'nonEmpty' },
    helpText: 'Overrides the partner external registration URL for this site.',
  },
  {
    id: 'location.pocContactId',
    label: 'Main on-site contact',
    scope: 'location',
    type: 'person',
    storage: { kind: 'locations', fieldPath: 'pocContactId' },
    systemRequired: false,
    gates: ['other'],
    completeness: { kind: 'nonEmpty' },
    helpText: 'Stores a partner contact ID applicable to this location.',
  },
  {
    id: 'location.wifiUsername',
    label: 'Wi-Fi username',
    scope: 'location',
    type: 'text',
    storage: { kind: 'locations', fieldPath: 'wifiUsername' },
    systemRequired: false,
    gates: ['other'],
    completeness: { kind: 'exists' },
  },
  {
    id: 'location.wifiPassword',
    label: 'Wi-Fi password',
    scope: 'location',
    type: 'text',
    storage: { kind: 'locations', fieldPath: 'wifiPassword' },
    systemRequired: false,
    gates: ['other'],
    completeness: { kind: 'exists' },
  },
  {
    id: 'location.parkingInstructions',
    label: 'Parking instructions',
    scope: 'location',
    type: 'text',
    storage: { kind: 'locations', fieldPath: 'parkingInstructions' },
    systemRequired: false,
    gates: ['other'],
    completeness: { kind: 'nonEmpty' },
  },
  {
    id: 'location.hallwayRules',
    label: 'Hallway rules',
    scope: 'location',
    type: 'text',
    storage: { kind: 'locations', fieldPath: 'hallwayRules' },
    systemRequired: false,
    gates: ['other'],
    completeness: { kind: 'nonEmpty' },
  },
  {
    id: 'location.bathroomRules',
    label: 'Bathroom rules',
    scope: 'location',
    type: 'text',
    storage: { kind: 'locations', fieldPath: 'bathroomRules' },
    systemRequired: false,
    gates: ['other'],
    completeness: { kind: 'nonEmpty' },
  },
  {
    id: 'location.hasProjectorOrTv',
    label: 'TV or projector available',
    scope: 'location',
    type: 'boolean',
    storage: { kind: 'locations', fieldPath: 'hasProjectorOrTv' },
    systemRequired: false,
    gates: ['other'],
    completeness: { kind: 'exists' },
  },
  {
    id: 'location.projectorConnectionInstructions',
    label: 'Projector/TV instructions',
    scope: 'location',
    type: 'text',
    storage: { kind: 'locations', fieldPath: 'projectorConnectionInstructions' },
    systemRequired: false,
    gates: ['other'],
    completeness: { kind: 'nonEmpty' },
  },
  {
    id: 'location.techTestedOnSite',
    label: 'Technology tested on site',
    scope: 'location',
    type: 'boolean',
    storage: { kind: 'locations', fieldPath: 'techTestedOnSite' },
    systemRequired: false,
    gates: ['other'],
    completeness: { kind: 'exists' },
  },
  {
    id: 'location.sitesWhitelisted',
    label: 'Overture sites whitelisted',
    scope: 'location',
    type: 'boolean',
    storage: { kind: 'locations', fieldPath: 'sitesWhitelisted' },
    systemRequired: false,
    gates: ['other'],
    completeness: { kind: 'exists' },
  },
];

const baseIds = new Set(BASE_PARTNER_LOGISTICS_CATALOG.map((definition) => definition.id));

export const PARTNER_LOGISTICS_CATALOG: ReadonlyArray<OpsLogisticDefinition> =
  Object.freeze([
    ...BASE_PARTNER_LOGISTICS_CATALOG,
    ...TRANSLATED_LEGACY_SCHOOL_LOGISTICS_CATALOG.filter((definition) => !baseIds.has(definition.id)),
  ]);

export const PARTNER_LOGISTICS_BY_ID: Readonly<Record<string, OpsLogisticDefinition>> =
  Object.freeze(
    PARTNER_LOGISTICS_CATALOG.reduce<Record<string, OpsLogisticDefinition>>((result, definition) => {
      result[definition.id] = definition;
      return result;
    }, {})
  );
